import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Navigation from './Navigation';
import Footer from './Footer';
import BreadIcon from './BreadIcon';
import ViennoiserieIcon from './ViennoiserieIcon';
import LowStockAlert from './LowStockAlert';
import { 
  TrendingUp, 
  Package, 
  ShoppingCart, 
  RotateCcw, 
  DollarSign,
  Wallet,
  Package2,
  TrendingDown,
  Cookie,
  Cake,
  Coffee
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const { API } = useContext(UserContext);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/summary`, {
        params: { date: selectedDate }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedDate]);

  const getCounterIcon = (counter) => {
    switch (counter) {
      case 'pain':
        return <BreadIcon className="text-white" />;
      case 'viennoiserie':
        return <ViennoiserieIcon className="text-white" />;
      case 'gateaux':
        return <Cake className="h-6 w-6" />;
      default:
        return <Package className="h-6 w-6" />;
    }
  };

  const getCounterDisplayName = (counter) => {
    const counterNames = {
      'pain': 'Pains',
      'viennoiserie': 'Viennoiserie',
      'gateaux': 'Gâteaux'
    };
    return counterNames[counter] || counter;
  };

  const getCounterColor = (counter) => {
    switch (counter) {
      case 'pain':
        return 'from-yellow-400 to-orange-500';
      case 'viennoiserie':
        return 'from-purple-400 to-pink-500';
      case 'gateaux':
        return 'from-pink-400 to-rose-500';
      default:
        return 'from-gray-400 to-gray-500';
    }
  };

  const formatCurrency = (amount) => {
    return `${amount.toLocaleString()} F CFA`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des données...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="content-wrapper px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="max-width-container flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 fade-in">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Tableau de Bord
            </h1>
            <p className="text-gray-600">
              Suivi de production en temps réel
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center space-x-0 sm:space-x-4 space-y-4 sm:space-y-0 mt-4 sm:mt-0">
            {/* Alerte Stock Faible */}
            <LowStockAlert threshold={5} autoCheck={true} />
            
            <div className="flex items-center space-x-4">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="date-picker"
              />
              <Button
                onClick={fetchDashboardData}
                variant="outline"
                size="sm"
                className="btn-hover"
              >
                Actualiser
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="dashboard-metrics grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
          <Card className="card-hover bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Chiffre d'Affaires
                  </p>
                  <p className="text-2xl font-bold text-green-600" data-testid="total-revenue">
                    {dashboardData ? formatCurrency(dashboardData.total_revenue) : '0 F CFA'}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Coût Matières Premières
                  </p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="total-supply-costs">
                    {dashboardData ? formatCurrency(dashboardData.total_supply_costs || 0) : '0 F CFA'}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Package2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Autres Dépenses
                  </p>
                  <p className="text-2xl font-bold text-red-600" data-testid="total-expenses">
                    {dashboardData ? formatCurrency(dashboardData.total_expenses || 0) : '0 F CFA'}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-red-400 to-red-500 rounded-full flex items-center justify-center">
                  <Wallet className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Coûts
                  </p>
                  <p className="text-2xl font-bold text-purple-600" data-testid="total-costs">
                    {dashboardData ? formatCurrency(dashboardData.total_costs || 0) : '0 F CFA'}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-purple-400 to-purple-500 rounded-full flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Bénéfice Net
                  </p>
                  <p className={`text-2xl font-bold ${dashboardData && dashboardData.net_result >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="net-result">
                    {dashboardData ? formatCurrency(dashboardData.net_result || 0) : '0 F CFA'}
                  </p>
                </div>
                <div className={`h-12 w-12 bg-gradient-to-r ${dashboardData && dashboardData.net_result >= 0 ? 'from-green-400 to-emerald-500' : 'from-red-400 to-red-500'} rounded-full flex items-center justify-center`}>
                  {dashboardData && dashboardData.net_result >= 0 ? 
                    <TrendingUp className="h-6 w-6 text-white" /> :
                    <TrendingDown className="h-6 w-6 text-white" />
                  }
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover bg-white/70 glass border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Vendu
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboardData ? 
                      dashboardData.counters.reduce((sum, counter) => sum + counter.total_sold, 0) 
                      : 0
                    }
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Counters Overview */}
        <div className="max-width-cards grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {dashboardData?.counters.map((counter, index) => (
            <Card key={counter.counter} className={`card-hover bg-white/70 glass border-0 shadow-lg slide-up`} style={{animationDelay: `${index * 0.1}s`}}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-3">
                    <div className={`h-10 w-10 bg-gradient-to-r ${getCounterColor(counter.counter)} rounded-lg flex items-center justify-center`}>
                      {getCounterIcon(counter.counter)}
                    </div>
                    {getCounterDisplayName(counter.counter)}
                  </CardTitle>
                  <Badge variant="secondary" className={`counter-${counter.counter}`}>
                    {counter.products.length} produits
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{counter.total_produced}</p>
                    <p className="text-xs text-blue-600 font-medium">Produits</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{counter.total_sold}</p>
                    <p className="text-xs text-green-600 font-medium">Vendus</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{counter.total_returned}</p>
                    <p className="text-xs text-red-600 font-medium">Retours</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{counter.total_remaining}</p>
                    <p className="text-xs text-orange-600 font-medium">Restants</p>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Chiffre d'affaires</span>
                    <span className="text-lg font-bold text-green-600">
                      {formatCurrency(counter.revenue)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="max-width-cards bg-white/70 glass border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Actions Rapides
            </CardTitle>
            <CardDescription>
              Accédez rapidement aux fonctions principales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <Link to="/stock">
                <Button className="w-full h-16 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg btn-hover" data-testid="stock-entry-btn">
                  <Package2 className="h-6 w-6 mr-2" />
                  Gestion Stock
                </Button>
              </Link>
              
              <Link to="/production">
                <Button className="w-full h-16 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-xl shadow-lg btn-hover" data-testid="production-entry-btn">
                  <Package className="h-6 w-6 mr-2" />
                  Saisie Production
                </Button>
              </Link>
              
              <Link to="/sales">
                <Button className="w-full h-16 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-xl shadow-lg btn-hover" data-testid="sales-entry-btn">
                  <ShoppingCart className="h-6 w-6 mr-2" />
                  Saisie Ventes
                </Button>
              </Link>
              
              <Link to="/returns">
                <Button className="w-full h-16 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-medium rounded-xl shadow-lg btn-hover" data-testid="returns-entry-btn">
                  <RotateCcw className="h-6 w-6 mr-2" />
                  Saisie Retours
                </Button>
              </Link>
              
              <Link to="/expenses">
                <Button className="w-full h-16 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-medium rounded-xl shadow-lg btn-hover" data-testid="expenses-entry-btn">
                  <Wallet className="h-6 w-6 mr-2" />
                  Saisie Dépenses
                </Button>
              </Link>
              
              <Link to="/reports">
                <Button className="w-full h-16 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white font-medium rounded-xl shadow-lg btn-hover" data-testid="reports-btn">
                  <TrendingUp className="h-6 w-6 mr-2" />
                  Rapports
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default Dashboard;